import { useTranslation } from 'react-i18next';

import type { Order } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Separator } from '@nucleus-commerce/ui/components/separator';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { outstanding } from '../schemas/order-rules';

function Row({
  label,
  value,
  emphasis,
  className,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-4 text-sm',
        emphasis && 'font-semibold',
        className,
      )}
    >
      <dt className={emphasis ? undefined : 'text-muted-foreground'}>{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}

/** Subtotal to outstanding; the method fee shows when the manual method charged one. */
export function OrderTotalsCard({ order }: { order: Order }) {
  const { t } = useTranslation('orders');
  const format = useFormat();
  const fee = order.payments.find(
    (p) => p.methodSnapshot && p.methodSnapshot.fee.amount > 0,
  )?.methodSnapshot;
  const balance = outstanding(order);

  return (
    <Card className="gap-3">
      <CardHeader>
        <CardTitle>{t('totals.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="flex flex-col gap-1.5">
          <Row label={t('totals.subtotal')} value={format.money(order.subtotal)} />
          {order.discount.amount !== 0 ? (
            <Row label={t('totals.discount')} value={`−${format.money(order.discount)}`} />
          ) : null}
          <Row
            label={
              order.shippingMethod
                ? t('totals.shippingWith', { method: order.shippingMethod.title })
                : t('totals.noShipping')
            }
            value={format.money(order.shipping)}
          />
          {fee ? (
            <Row label={t('totals.fee', { method: fee.name })} value={format.money(fee.fee)} />
          ) : null}
          <Row label={t('totals.tax')} value={format.money(order.tax)} />
          <Separator className="my-1" />
          <Row label={t('totals.total')} value={format.money(order.total)} emphasis />
          <Row label={t('totals.paid')} value={format.money(order.paid)} />
          {order.refunded.amount !== 0 ? (
            <Row label={t('totals.refunded')} value={format.money(order.refunded)} />
          ) : null}
          <Separator className="my-1" />
          {balance.amount > 0 ? (
            <Row
              label={t('totals.outstanding')}
              value={format.money(balance)}
              emphasis
              className="text-warning-foreground dark:text-warning"
            />
          ) : (
            <Row label={t('totals.outstanding')} value={t('totals.settled')} />
          )}
        </dl>
      </CardContent>
    </Card>
  );
}
